import {
  LevelRewardInput,
  LevelRewardInputType,
  PraAmountMode,
  PraBoxPoolInput,
  PraRandomBoxInput,
  PraSection,
  PraSectionConfigDataFragment,
  UpdatePraSectionConfigInput,
} from '../../../../../__gqltypes__';

export type PraSectionConfigData = PraSectionConfigDataFragment;

export const PRA_SECTION_LABELS: Record<PraSection, string> = {
  [PraSection.FREE]: 'Free Rewards',
  [PraSection.CHAINED]: 'Chained Rewards',
};

// Mirrors the server-side validation constants (SP3BE progressiveAds/config.ts):
// the resolver is the final authority, these only drive canSave.
export const PRA_MIN_COOLDOWN_SECONDS = 60;
// JS 32-bit bitwise ops cap the claimedMask at index 31 (1 << 32 wraps) — the
// server enforces the same limit (SP3BE progressiveAds/config.ts).
export const PRA_MAX_MILESTONES = 31;

// Default cooldowns from the GDD: 3h FREE / 8h CHAINED (admin-configurable to seconds).
export const PRA_DEFAULT_COOLDOWN_SECONDS: Record<PraSection, number> = {
  [PraSection.FREE]: 10800,
  [PraSection.CHAINED]: 28800,
};

// The PRA reward allowlist (plan §3) — AppUser-resident currencies only. Mirrors
// SP3BE PRA_ALLOWED_REWARD_TYPES (progressiveAds/utils.ts).
export const PRA_ALLOWED_REWARD_TYPES: LevelRewardInputType[] = [
  LevelRewardInputType.CURRENCY_COIN,
  LevelRewardInputType.CURRENCY_DIAMOND,
  LevelRewardInputType.CURRENCY_BOOST,
  LevelRewardInputType.CURRENCY_TOURNAMENT_TICKET,
  LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_BRONZE,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_SILVER,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_GOLD,
  LevelRewardInputType.CURRENCY_SHARD_BRONZE,
  LevelRewardInputType.CURRENCY_SHARD_SILVER,
  LevelRewardInputType.CURRENCY_SHARD_GOLD,
];

// ---------------------------------------------------------------------------------------------------------------------
// Editor-side (runtime) shapes — Marathon `_clientId` pattern (see Marathon/utils.ts):
// a stable editor-only row identity so React keys follow a row across removals, stripped on save.
// ---------------------------------------------------------------------------------------------------------------------

let clientIdCounter = 0;
export function nextClientId(): string {
  clientIdCounter += 1;
  return `pra${clientIdCounter}`;
}

export type PraRewardRow = {
  _clientId: string;
  isRandomBox: boolean;
  reward: LevelRewardInput | null; // null only when the row is the Random Box
};

// Box pool item — the reward is a type+itemId reference. The amount is either
// EXACT or a RANGE, an explicit per-item toggle (D29); rows in one pool mix freely.
export type PraPoolItemRow = {
  _clientId: string;
  weight: number;
  rewardType: LevelRewardInputType;
  itemId: string | null;
  amountMode: PraAmountMode;
  amount: number; // EXACT mode
  minAmount: number; // RANGE mode
  maxAmount: number; // RANGE mode
};

export type PraBoxPoolDraft = {
  itemsPerOpening: number;
  items: PraPoolItemRow[];
};

export type PraBoxDraft = {
  freePool: PraBoxPoolDraft; // the milestone box (ad for basic / direct for ad-free, D20)
  purchasePool: PraBoxPoolDraft; // Diamond-purchased boxes — every purchase draws from this same pool (D21)
  priceDiamonds: number;
  purchaseCapPerCycle: number;
};

export type PraSectionDraft = {
  section: PraSection;
  enabled: boolean;
  cooldownSeconds: number;
  rewards: PraRewardRow[];
  // Present iff the last rewards entry is the Random Box (entry ⇔ config, both ways).
  randomBox: PraBoxDraft | null;
};

// PREFILL for a new box config only (D11) — priceDiamonds stays fully admin-configurable
// per section and is never hardcoded anywhere else.
export const PRA_NEW_BOX_PRICE_PREFILL = 25;

export function newRewardRow(): PraRewardRow {
  return {
    _clientId: nextClientId(),
    isRandomBox: false,
    reward: { type: LevelRewardInputType.CURRENCY_COIN, amount: 10 },
  };
}

export function newRandomBoxRow(): PraRewardRow {
  return { _clientId: nextClientId(), isRandomBox: true, reward: null };
}

export function newPoolItemRow(): PraPoolItemRow {
  return {
    _clientId: nextClientId(),
    weight: 10,
    rewardType: LevelRewardInputType.CURRENCY_COIN,
    itemId: null,
    amountMode: PraAmountMode.EXACT,
    amount: 1,
    minAmount: 1,
    maxAmount: 1,
  };
}

export function defaultBoxDraft(): PraBoxDraft {
  return {
    freePool: { itemsPerOpening: 1, items: [newPoolItemRow()] },
    purchasePool: { itemsPerOpening: 1, items: [newPoolItemRow()] },
    priceDiamonds: PRA_NEW_BOX_PRICE_PREFILL,
    purchaseCapPerCycle: 0, // 0 = extra purchases disabled until LiveOps sets a cap deliberately
  };
}

export function defaultSectionDraft(section: PraSection): PraSectionDraft {
  return {
    section,
    enabled: false,
    cooldownSeconds: PRA_DEFAULT_COOLDOWN_SECONDS[section],
    rewards: [newRewardRow()],
    randomBox: null,
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// Config <-> draft converters (build fresh objects with explicit fields: strips __typename
// so the result can be sent back as an input untouched)
// ---------------------------------------------------------------------------------------------------------------------

type ConfigRewardShape = { type: LevelRewardInputType; amount: number; itemId?: string | null };

function rewardToInput(reward: ConfigRewardShape): LevelRewardInput {
  return { type: reward.type, amount: reward.amount, itemId: reward.itemId ?? null };
}

type ConfigPoolShape = NonNullable<PraSectionConfigData['randomBox']>['freePool'];

function poolViewToDraft(pool: ConfigPoolShape): PraBoxPoolDraft {
  return {
    itemsPerOpening: pool.itemsPerOpening,
    items: pool.contents.map((item) => ({
      _clientId: nextClientId(),
      weight: item.weight,
      rewardType: item.reward.type,
      itemId: item.reward.itemId ?? null,
      amountMode: item.amountMode,
      amount: item.amount ?? 1,
      minAmount: item.minAmount ?? 1,
      maxAmount: item.maxAmount ?? 1,
    })),
  };
}

export function boxViewToDraft(box: NonNullable<PraSectionConfigData['randomBox']>): PraBoxDraft {
  return {
    freePool: poolViewToDraft(box.freePool),
    purchasePool: poolViewToDraft(box.purchasePool),
    priceDiamonds: box.priceDiamonds,
    purchaseCapPerCycle: box.purchaseCapPerCycle,
  };
}

export function configToDraft(config: PraSectionConfigData): PraSectionDraft {
  return {
    section: config.section,
    enabled: config.enabled,
    cooldownSeconds: config.cooldownSeconds,
    rewards: config.rewards.map((entry) => ({
      _clientId: nextClientId(),
      isRandomBox: entry.isRandomBox,
      reward: entry.reward ? rewardToInput(entry.reward) : null,
    })),
    randomBox: config.randomBox ? boxViewToDraft(config.randomBox) : null,
  };
}

function poolDraftToInput(pool: PraBoxPoolDraft): PraBoxPoolInput {
  return {
    itemsPerOpening: pool.itemsPerOpening,
    contents: pool.items.map((item) => ({
      weight: item.weight,
      reward: { type: item.rewardType, itemId: item.itemId },
      amountMode: item.amountMode,
      // Send only the active mode's fields — admin intent stays unambiguous.
      ...(item.amountMode === PraAmountMode.EXACT
        ? { amount: item.amount }
        : { minAmount: item.minAmount, maxAmount: item.maxAmount }),
    })),
  };
}

export function boxDraftToInput(box: PraBoxDraft): PraRandomBoxInput {
  return {
    freePool: poolDraftToInput(box.freePool),
    purchasePool: poolDraftToInput(box.purchasePool),
    priceDiamonds: box.priceDiamonds,
    purchaseCapPerCycle: box.purchaseCapPerCycle,
  };
}

// Namespace is omitted on purpose: the server defaults to its own namespace.
export function draftToInput(draft: PraSectionDraft): UpdatePraSectionConfigInput {
  return {
    section: draft.section,
    enabled: draft.enabled,
    cooldownSeconds: draft.cooldownSeconds,
    rewards: draft.rewards.map((row) =>
      row.isRandomBox
        ? { isRandomBox: true }
        : { isRandomBox: false, reward: row.reward ? { ...row.reward } : null }
    ),
    randomBox: draft.randomBox ? boxDraftToInput(draft.randomBox) : null,
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// Cooldown editor helpers — days/hours/minutes/seconds summing to cooldownSeconds (QA requirement)
// ---------------------------------------------------------------------------------------------------------------------

export type CooldownParts = { days: number; hours: number; minutes: number; seconds: number };

export function secondsToParts(totalSeconds: number): CooldownParts {
  const safe = Math.max(0, Math.floor(totalSeconds));
  return {
    days: Math.floor(safe / 86400),
    hours: Math.floor((safe % 86400) / 3600),
    minutes: Math.floor((safe % 3600) / 60),
    seconds: safe % 60,
  };
}

export function partsToSeconds({ days, hours, minutes, seconds }: CooldownParts): number {
  return days * 86400 + hours * 3600 + minutes * 60 + seconds;
}

// ---------------------------------------------------------------------------------------------------------------------
// Maintenance / safe-to-edit (D8): maintenanceSince + this section's cooldownSeconds + 15 min buffer
// ---------------------------------------------------------------------------------------------------------------------

export const PRA_SAFE_EDIT_BUFFER_MS = 15 * 60 * 1000;

export function getSafeToEditAt(
  maintenanceSince: Date | string | number | null | undefined,
  cooldownSeconds: number
): Date | null {
  if (maintenanceSince == null) {
    return null;
  }
  return new Date(new Date(maintenanceSince).getTime() + cooldownSeconds * 1000 + PRA_SAFE_EDIT_BUFFER_MS);
}

// "safe in HH:MM:SS" — hours unbounded (a 8h cooldown + buffer never wraps a day badly).
export function formatHms(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Save-time validation — mirrors SP3BE validatePraSectionConfig (progressiveAds/utils.ts);
// the server stays the final authority, this only drives canSave + the red Alert.
// ---------------------------------------------------------------------------------------------------------------------

export function validatePraReward(reward: LevelRewardInput, label: string): string[] {
  const errors: string[] = [];
  if (!PRA_ALLOWED_REWARD_TYPES.includes(reward.type)) {
    errors.push(`${label}: reward type ${reward.type} is not allowed in Progressive Rewarded Ads.`);
  }
  if (reward.type === LevelRewardInputType.CURRENCY_BOOST && !(reward.itemId ?? '').trim()) {
    errors.push(`${label}: a Booster reward needs its boost type (itemId).`);
  }
  if (reward.type === LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT && !(reward.itemId ?? '').trim()) {
    errors.push(`${label}: a Fragment reward needs its categoryId (itemId).`);
  }
  return errors;
}

export const PRA_FREE_POOL_LABEL = 'Milestone box pool (ad/free)';
export const PRA_PURCHASE_POOL_LABEL = 'Purchased box pool (Diamonds)';

// Derived read-only chance column — ONE decimal (D16). Independently rounded values may
// not total exactly 100.0; the display does NOT rescale ("chances are rounded" footnote).
export function formatChancePercent(weight: number, totalWeight: number): string {
  if (!Number.isFinite(weight) || !Number.isFinite(totalWeight) || totalWeight <= 0) {
    return '—';
  }
  return `${((weight / totalWeight) * 100).toFixed(1)}%`;
}

// Mirrors SP3BE validatePraBoxPool (progressiveAds/utils.ts) — blocking errors only;
// the itemsPerOpening-vs-distinct-items guard is a non-blocking WARNING (D27), see
// getPraSectionDraftWarnings.
function validateBoxPoolDraft(pool: PraBoxPoolDraft, label: string): string[] {
  const errors: string[] = [];
  if (pool.items.length === 0) {
    errors.push(`${label}: the pool needs at least one item.`);
    return errors;
  }
  if (!Number.isInteger(pool.itemsPerOpening) || pool.itemsPerOpening < 1) {
    errors.push(`${label}: itemsPerOpening must be an integer >= 1.`);
  }
  pool.items.forEach((item, index) => {
    const itemLabel = `${label} item #${index + 1}`;
    if (!(item.weight > 0) || !Number.isFinite(item.weight)) {
      errors.push(`${itemLabel}: weight must be a positive finite number.`);
    }
    if (item.amountMode === PraAmountMode.EXACT) {
      if (!Number.isInteger(item.amount) || item.amount < 1) {
        errors.push(`${itemLabel}: Exact amount must be an integer >= 1.`);
      }
    } else {
      if (!Number.isInteger(item.minAmount) || item.minAmount < 1) {
        errors.push(`${itemLabel}: minAmount must be an integer >= 1.`);
      }
      if (!Number.isInteger(item.maxAmount) || item.maxAmount < item.minAmount) {
        errors.push(`${itemLabel}: maxAmount must be an integer >= minAmount.`);
      }
    }
    errors.push(...validatePraReward({ type: item.rewardType, amount: 1, itemId: item.itemId }, itemLabel));
  });
  return errors;
}

export function validatePraSectionDraft(draft: PraSectionDraft): string[] {
  const errors: string[] = [];

  if (!Number.isInteger(draft.cooldownSeconds) || draft.cooldownSeconds < PRA_MIN_COOLDOWN_SECONDS) {
    errors.push(`cooldownSeconds must be an integer >= ${PRA_MIN_COOLDOWN_SECONDS}.`);
  }

  if (draft.section === PraSection.FREE) {
    // GD invariant D12: the FREE section has exactly ONE reward, always.
    if (draft.rewards.length !== 1) {
      errors.push('FREE section must have exactly one reward (GD invariant).');
    }
  } else if (draft.rewards.length < 1 || draft.rewards.length > PRA_MAX_MILESTONES) {
    errors.push(`CHAINED section must have between 1 and ${PRA_MAX_MILESTONES} milestones (bitmask cap).`);
  }

  draft.rewards.forEach((row, index) => {
    const label = `reward #${index + 1}`;
    if (row.isRandomBox) {
      if (index !== draft.rewards.length - 1) {
        errors.push(`${label}: the Random Box must be the LAST reward in the sequence.`);
      }
      return;
    }
    if (!row.reward) {
      errors.push(`${label}: missing reward.`);
      return;
    }
    if (!Number.isInteger(row.reward.amount) || row.reward.amount < 1) {
      errors.push(`${label}: amount must be an integer >= 1.`);
    }
    errors.push(...validatePraReward(row.reward, label));
  });

  if (draft.rewards.filter((row) => row.isRandomBox).length > 1) {
    errors.push('Only one Random Box entry is allowed per section.');
  }

  // Box entry in the sequence ⇔ box config present (both ways, like the resolver).
  const hasBoxEntry = draft.rewards.some((row) => row.isRandomBox);
  if (hasBoxEntry && !draft.randomBox) {
    errors.push('The sequence ends in a Random Box but no box configuration was provided.');
  }
  if (!hasBoxEntry && draft.randomBox) {
    errors.push('A box configuration was provided but no rewards entry is the Random Box.');
  }

  if (draft.randomBox) {
    errors.push(...validateBoxPoolDraft(draft.randomBox.freePool, PRA_FREE_POOL_LABEL));
    errors.push(...validateBoxPoolDraft(draft.randomBox.purchasePool, PRA_PURCHASE_POOL_LABEL));
    if (!Number.isInteger(draft.randomBox.priceDiamonds) || draft.randomBox.priceDiamonds < 1) {
      errors.push('priceDiamonds must be an integer >= 1 (25 is only the form prefill).');
    }
    if (!Number.isInteger(draft.randomBox.purchaseCapPerCycle) || draft.randomBox.purchaseCapPerCycle < 0) {
      errors.push('purchaseCapPerCycle must be an integer >= 0 (0 disables extra purchases).');
    }
  }

  return errors;
}

// Warning-only, never blocks save (D5 — kept as a typo catch, C3): consecutive milestones
// of the same reward type/item with a DECREASING amount. Balancing is LiveOps' call;
// cross-type values are not comparable, so only same-type neighbours are checked.
export function getPraSectionDraftWarnings(draft: PraSectionDraft): string[] {
  const warnings: string[] = [];

  // Non-blocking human-error guard (D27): without-replacement drawing cannot be satisfied
  // past the distinct-item count; the server runtime clamps the roll to the pool size.
  const poolClampWarning = (pool: PraBoxPoolDraft, label: string) => {
    if (pool.items.length > 0 && Number.isInteger(pool.itemsPerOpening) && pool.itemsPerOpening > pool.items.length) {
      warnings.push(
        `${label}: itemsPerOpening (${pool.itemsPerOpening}) exceeds the pool's distinct items ` +
          `(${pool.items.length}) — the roll will clamp to ${pool.items.length}.`
      );
    }
  };
  if (draft.randomBox) {
    poolClampWarning(draft.randomBox.freePool, PRA_FREE_POOL_LABEL);
    poolClampWarning(draft.randomBox.purchasePool, PRA_PURCHASE_POOL_LABEL);
  }

  for (let i = 1; i < draft.rewards.length; ++i) {
    const prev = draft.rewards[i - 1].reward;
    const current = draft.rewards[i].reward;
    if (
      prev &&
      current &&
      prev.type === current.type &&
      (prev.itemId ?? null) === (current.itemId ?? null) &&
      current.amount < prev.amount
    ) {
      warnings.push(
        `reward #${i + 1}: amount (${current.amount}) is lower than the previous same-type milestone ` +
          `(${prev.amount}) — check for a typo.`
      );
    }
  }
  return warnings;
}
